import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, CircularProgress, Stack, Typography } from "@mui/material";
import { startStandaloneConnect } from "@/utils/standaloneConnect";

interface StandaloneConnectBootstrapProps {
  children: React.ReactNode;
}

function getExternalAuthId(search: string): string {
  return new URLSearchParams(search).get("external_auth_id")?.trim() ?? "";
}

function getUrlWithoutExternalAuthId(pathname: string, search: string, hash: string): string {
  const nextParams = new URLSearchParams(search);
  nextParams.delete("external_auth_id");

  const query = nextParams.toString();
  return `${pathname}${query ? `?${query}` : ""}${hash}`;
}

function StandaloneConnectInterstitial({
  errorMessage,
  onRetry,
}: {
  errorMessage: string | null;
  onRetry: () => void;
}) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={3} sx={{ minHeight: "100vh", px: 2 }}>
      {errorMessage ? (
        <>
          <Typography textAlign="center">Unable to start external sign-in</Typography>
          <Typography color="text.secondary" textAlign="center">
            {errorMessage}
          </Typography>
          <Button variant="contained" onClick={onRetry}>
            Retry
          </Button>
        </>
      ) : (
        <>
          <CircularProgress />
          <Typography textAlign="center">Logging in with Financeable</Typography>
        </>
      )}
    </Stack>
  );
}

const StandaloneConnectBootstrap = ({ children }: StandaloneConnectBootstrapProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const externalAuthId = getExternalAuthId(location.search);
  const [startError, setStartError] = useState<string | null>(null);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const startedExternalAuthIdRef = useRef<string | null>(null);

  const retryStartConnect = useCallback(() => {
    startedExternalAuthIdRef.current = null;
    setStartError(null);
    setRetryAttempt((attempt) => attempt + 1);
  }, []);

  useEffect(() => {
    if (!externalAuthId) {
      startedExternalAuthIdRef.current = null;
      return;
    }

    if (startedExternalAuthIdRef.current === externalAuthId) {
      return;
    }
    startedExternalAuthIdRef.current = externalAuthId;

    let isMounted = true;

    const startConnect = async () => {
      try {
        setStartError(null);
        const result = await startStandaloneConnect(externalAuthId);
        if (!isMounted) {
          return;
        }

        if (result.redirectUrl) {
          window.location.replace(result.redirectUrl);
          return;
        }

        navigate(getUrlWithoutExternalAuthId(location.pathname, location.search, location.hash), { replace: true });
      } catch (error) {
        if (isMounted) {
          console.error("Unable to start external sign-in", error);
          setStartError(error instanceof Error ? error.message : "Unable to start external sign-in");
        }
      }
    };

    startConnect();

    return () => {
      isMounted = false;
    };
  }, [externalAuthId, location.hash, location.pathname, location.search, navigate, retryAttempt]);

  if (externalAuthId) {
    return <StandaloneConnectInterstitial errorMessage={startError} onRetry={retryStartConnect} />;
  }

  return <>{children}</>;
};

export { StandaloneConnectBootstrap };
